import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import {
  ShoppingCartIcon,
  CubeIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
} from '@heroicons/react/24/outline';
import { SUPABASE_URL, supabaseAnonHeaders } from '../utils/env';
import { resilientFetch } from '../utils/supabaseClient';

/**
 * Storefront Page — Public page showing a contractor's products/services
 * URL: /store/:companyId
 * TODO: In production, use an opaque slug or token instead of raw companyId
 */
const Storefront = () => {
  const { companyId } = useParams();
  const [searchParams] = useSearchParams();
  const [store, setStore] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [purchasing, setPurchasing] = useState(null); // product ID being purchased

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadStore(); }, [companyId]);

  // Check for payment success
  const paymentSuccess = searchParams.get('session_id');

  const loadStore = async () => {
    try {
      setLoading(true);
      setError(null);

      // Supabase Edge Function calls require Authorization + apikey headers,
      // even for unauthenticated public pages — the gateway returns 401
      // UNAUTHORIZED_NO_AUTH_HEADER without them. Anon key is correct here
      // because this page is intentionally public.
      const response = await resilientFetch(
        `${SUPABASE_URL}/functions/v1/stripe-storefront?company_id=${companyId}`,
        { headers: supabaseAnonHeaders() }
      );

      const result = await response.json();
      if (!result.success) throw new Error(result.error || 'Failed to load store');
      setStore(result.data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handlePurchase = async (product) => {
    try {
      setPurchasing(product.id);
      setError(null);

      const response = await resilientFetch(
        `${SUPABASE_URL}/functions/v1/stripe-create-checkout`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...supabaseAnonHeaders(),
          },
          body: JSON.stringify({
            amount: product.amount, // already in cents
            companyId,
            jobTitle: product.name,
            description: product.description || product.name,
          }),
        }
      );

      const result = await response.json();
      if (!result.success) throw new Error(result.error || 'Failed to create checkout');

      if (result.data?.url) {
        window.location.href = result.data.url;
      }
    } catch (err) {
      setError(err.message);
      setPurchasing(null);
    }
  };

  const formatCurrency = (amount, currency = 'usd') => {
    if (!amount) return '$0.00';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
    }).format(amount / 100);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (error && !store) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-sm p-8 max-w-md text-center">
          <ExclamationCircleIcon className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-lg font-semibold text-gray-900 mb-2">Store Not Found</h2>
          <p className="text-gray-600">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm">
        <div className="max-w-5xl mx-auto px-4 py-6">
          <h1 className="text-2xl font-bold text-gray-900">{store?.companyName}</h1>
          <p className="text-gray-500 mt-1">Products & Services</p>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-8">
        {/* Payment Success Banner */}
        {paymentSuccess && (
          <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg flex items-center gap-3">
            <CheckCircleIcon className="w-6 h-6 text-green-600" />
            <div>
              <div className="font-medium text-green-800">Payment Successful!</div>
              <div className="text-sm text-green-700">Thank you for your purchase. You'll receive a confirmation email shortly.</div>
            </div>
          </div>
        )}

        {error && (
          <div className="mb-6 p-4 bg-red-50 text-red-700 rounded-lg">{error}</div>
        )}

        {/* Products Grid */}
        {store?.products?.length === 0 ? (
          <div className="text-center py-16">
            <ShoppingCartIcon className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h2 className="text-lg font-medium text-gray-900">No products available</h2>
            <p className="text-gray-500 mt-1">Check back later for available services.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {store?.products?.map((product) => (
              <div
                key={product.id}
                className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden hover:shadow-md transition-shadow"
              >
                {product.images?.[0] ? (
                  <img
                    src={product.images[0]}
                    alt={product.name}
                    className="w-full h-48 object-cover"
                  />
                ) : (
                  <div className="w-full h-48 bg-gradient-to-br from-indigo-100 to-purple-100 flex items-center justify-center">
                    <CubeIcon className="w-16 h-16 text-indigo-300" />
                  </div>
                )}
                <div className="p-5">
                  <h3 className="font-semibold text-gray-900 text-lg">{product.name}</h3>
                  {product.description && (
                    <p className="text-gray-600 text-sm mt-1 line-clamp-2">{product.description}</p>
                  )}
                  <div className="mt-4 flex items-center justify-between">
                    <span className="text-2xl font-bold text-gray-900">
                      {formatCurrency(product.amount, product.currency)}
                    </span>
                    <button
                      onClick={() => handlePurchase(product)}
                      disabled={purchasing === product.id}
                      className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 text-sm font-medium transition-colors"
                    >
                      {purchasing === product.id ? 'Processing...' : 'Buy Now'}
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Storefront;
